import { create } from 'zustand'

type Theme = 'light' | 'dark' | 'system'

interface Toast {
  id: string
  title: string
  description?: string
  variant?: 'default' | 'success' | 'warning' | 'error' | 'database_down' | 'input_error' | 'delete_success'
}

interface UIState {
  theme: Theme
  sidebarOpen: boolean
  toasts: Toast[]
  setTheme: (theme: Theme) => void
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
}

// Helper function to apply theme to document
function applyTheme(theme: Theme) {
  const root = window.document.documentElement
  root.classList.remove('light', 'dark')
  if (theme === 'system') {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
    root.classList.add(systemTheme)
  } else {
    root.classList.add(theme)
  }
}

// Get initial theme from localStorage or default to 'system'
function getInitialTheme(): Theme {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('theme') as Theme | null
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored
    }
  }
  return 'system'
}

// Initialize theme on module load
const initialTheme = getInitialTheme()
if (typeof window !== 'undefined') {
  applyTheme(initialTheme)

  // Listen for system theme changes when in 'system' mode
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const currentTheme = useUIStore.getState().theme
    if (currentTheme === 'system') {
      applyTheme('system')
    }
  })
}

export const useUIStore = create<UIState>((set) => ({
  theme: initialTheme,
  sidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  toasts: [],

  setTheme: (theme) => {
    set({ theme })
    // Persist to localStorage
    localStorage.setItem('theme', theme)
    // Apply theme to document
    applyTheme(theme)
  },

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  addToast: (toast) => {
    const id = Math.random().toString(36).substring(7)
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }))
    const isDesktop = window.matchMedia('(min-width: 640px)').matches
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }))
    }, isDesktop ? 5000 : 1700)
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
}))
